import type { RefObject } from "react";
import { useCallback, useRef, useState } from "react";

import { Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox, DotsLoader, IconButton, Popover, PopoverTrigger } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useElementSize } from "app/pages/sales-intelligence/hooks/useElementSize";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { AddNewAttendeeItem } from "app/pages/sales-intelligence/pages/ai-agent/components/AddNewAttendeeItem/AddNewAttendeeItem";
import { AttendeesLimitTooltip } from "app/pages/sales-intelligence/pages/ai-agent/components/AttendeesLimitTooltip/AttendeesLimitTooltip";

import {
    StyledAttendeeOption,
    StyledAttendeeOptionContent,
    StyledAttendeesPopoverContent,
    StyledAttendeesSearchWrapper,
    StyledAttendeesTextField,
} from "./styles";
import { useAttendeesSearchKeyboard } from "./useAttendeesSearchKeyboard";
import type { SearchErrorVariant } from "./utils";

// The "add as new" row is always the first navigable item, search results follow it.
const ADD_NEW_INDEX = 0;

// TextField forces a 36px height on any button inside its input group; matching it keeps the
// clear button round instead of stretching IconButton's default 32px square.
const CLEAR_BUTTON_SIZE = 36;

type Props = {
    search: string;
    options: Contact[];
    isLimitReached: boolean;
    isSearching: boolean;
    hasNoResults: boolean;
    hasError: boolean;
    errorType?: SearchErrorVariant;
    onSearchChange: (value: string) => void;
    onOptionClick: (contact: Contact) => void;
    onAddNew: () => void;
};

export const AttendeesSearch = ({
    search,
    options,
    isLimitReached,
    isSearching,
    hasNoResults,
    hasError,
    errorType,
    onSearchChange,
    onOptionClick,
    onAddNew,
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);
    const { ref: inputRef, size: inputSize } = useElementSize();

    const isPopoverOpen = isOpen && search.trim().length > 0;
    const isOptionDisabled = (contact: Contact): boolean => isLimitReached && !contact.isSelected;
    const disabledFlags = [isLimitReached, ...options.map(isOptionDisabled)];

    const handleSelectByIndex = useCallback(
        (index: number): void => {
            if (index === ADD_NEW_INDEX) {
                onAddNew();
                return;
            }
            onOptionClick(options[index - 1]);
        },
        [options, onAddNew, onOptionClick],
    );

    const handleClose = useCallback((): void => setIsOpen(false), []);

    const { activeIndex, listRef, handleKeyDown, handleItemHover } = useAttendeesSearchKeyboard({
        isOpen: isPopoverOpen,
        disabledFlags,
        onSelect: handleSelectByIndex,
        onClose: handleClose,
    });

    const handleSearchChange = (value: string) => {
        onSearchChange(value);
        setIsOpen(true);
    };

    const handleOpenChange = (open: boolean, event?: Event) => {
        const isInputPointerEvent =
            event?.type !== "keydown" && inputWrapperRef.current?.contains(event?.target as Node);

        if (!open && isInputPointerEvent) {
            return;
        }
        setIsOpen(open);
    };

    const handleClear = () => {
        onSearchChange("");
        setIsOpen(false);
    };

    return (
        <StyledAttendeesSearchWrapper
            ref={inputWrapperRef}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            data-automation="ai-agent-attendees-search"
        >
            <StyledAttendeesTextField
                ref={inputRef}
                value={search}
                placeholder={translate("si.pages.ai_agent.form.keyAttendees.search.placeholder")}
                startAddon={<Search size="sm" />}
                endAddon={
                    <Flex
                        alignItems="center"
                        gap="4px"
                    >
                        {isSearching ? <DotsLoader /> : null}
                        {search ? (
                            <IconButton
                                iconName="clear"
                                iconSize="xs"
                                width={CLEAR_BUTTON_SIZE}
                                height={CLEAR_BUTTON_SIZE}
                                type="flat"
                                onClick={handleClear}
                                data-automation="ai-agent-attendees-search-clear"
                            />
                        ) : null}
                    </Flex>
                }
                isInvalid={!!errorType}
                type="text"
                onChange={handleSearchChange}
            />
            <Popover
                isClickOutsideEnabled
                open={isPopoverOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                offset={0}
                initialFocus={-1}
            >
                <PopoverTrigger elementRef={inputWrapperRef as RefObject<HTMLElement>} />
                <StyledAttendeesPopoverContent
                    $width={inputSize?.width}
                    data-automation="ai-agent-attendees-dropdown"
                >
                    {hasError ? (
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.red[400]}
                            padding="12px 16px"
                            display="block"
                            data-automation="ai-agent-attendees-search-request-error"
                        >
                            {translate("si.common.toast.error")}
                        </Text>
                    ) : null}
                    {hasNoResults ? (
                        <Text
                            fontVariant="body2"
                            color={errorType ? colorsPalettes.red[400] : colorsPalettes.carbon[300]}
                            padding="12px 16px 4px"
                            display="block"
                        >
                            {errorType
                                ? translate("si.pages.ai_agent.form.keyAttendees.search.error", { errorType })
                                : translate("si.pages.ai_agent.form.keyAttendees.search.no_results")}
                        </Text>
                    ) : null}
                    {hasNoResults ? (
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                            padding="0 16px 12px"
                            display="block"
                        >
                            {translate("si.pages.ai_agent.form.keyAttendees.search.no_results.second")}
                        </Text>
                    ) : null}
                    <div
                        ref={listRef}
                        role="listbox"
                        aria-label={translate("si.pages.ai_agent.form.keyAttendees.title")}
                    >
                        <AddNewAttendeeItem
                            search={search}
                            isLimitReached={isLimitReached}
                            isActive={activeIndex === ADD_NEW_INDEX}
                            onClick={onAddNew}
                            onHover={() => handleItemHover(ADD_NEW_INDEX)}
                        />
                        {options.map((contact, index) => {
                            const isDisabled = isOptionDisabled(contact);
                            const navIndex = index + 1;

                            return (
                                <AttendeesLimitTooltip
                                    key={contact.id}
                                    isEnabled={isDisabled}
                                >
                                    <StyledAttendeeOption
                                        isDisabled={isDisabled}
                                        $isActive={activeIndex === navIndex}
                                        role="option"
                                        aria-selected={contact.isSelected}
                                        aria-disabled={isDisabled}
                                        onClick={() => onOptionClick(contact)}
                                        onMouseEnter={() => handleItemHover(navIndex)}
                                        data-automation={`ai-agent-attendees-option-${index}`}
                                    >
                                        <Checkbox
                                            selected={contact.isSelected}
                                            isDisabled={isDisabled}
                                        />
                                        <StyledAttendeeOptionContent>
                                            <Text
                                                fontVariant="input2"
                                                color={colorsPalettes.carbon[500]}
                                            >
                                                {contact.name}
                                            </Text>
                                            <Text
                                                fontVariant="listItem3"
                                                color={colorsPalettes.carbon[300]}
                                            >
                                                {contact.title}
                                            </Text>
                                        </StyledAttendeeOptionContent>
                                    </StyledAttendeeOption>
                                </AttendeesLimitTooltip>
                            );
                        })}
                    </div>
                </StyledAttendeesPopoverContent>
            </Popover>
        </StyledAttendeesSearchWrapper>
    );
};
